/**
 * DV-AUTH-004: Auth Status Report Hook
 *
 * Provides comprehensive authentication diagnostics and status reporting
 * for troubleshooting auth flows, session management, and PKCE issues.
 */

import { useCallback } from 'react';
import { useServerAuth } from './useAuth-cookie';
import { getAuthReadinessStatus } from '@/lib/auth-readiness';
import { buildApiUrl, API_ENDPOINTS, getApiHeadersGet } from '@/lib/api-base';

export interface AuthStatusReport {
  timestamp: string;
  session: {
    isAuthenticated: boolean;
    isLoading: boolean;
    hasError: boolean;
    user?: {
      id: string;
      email: string;
      role?: string;
      isAdmin: boolean;
    };
    error?: {
      type: string;
      message: string;
      status: number;
      correlationId?: string;
    };
  };
  readiness: {
    authReady: boolean;
    authCheckComplete: boolean;
    isMarkingReady: boolean;
    pendingOperations: number;
    callbackCount: number;
  };
  environment: {
    userAgent: string;
    platform: string;
    cookiesEnabled: boolean;
    javascriptEnabled: boolean;
    localStorage: {
      available: boolean;
      quotaExceeded: boolean;
    };
    sessionStorage: {
      available: boolean;
      quotaExceeded: boolean;
    };
  };
  network: {
    online: boolean;
    connection?: {
      effectiveType?: string;
      downlink?: number;
      rtt?: number;
    };
  };
  cors: {
    credentials: 'include';
    headers: {
      hasContentType: boolean;
      hasAuthorization: boolean;
      customHeaders: string[];
    };
  };
  cookies: {
    document: {
      available: boolean;
      count: number;
      hasAuthCookies: boolean;
    };
    sameSite: {
      supported: boolean;
      policy: string;
    };
    echo: {
      available: boolean;
      status: number | null;
      receivedCookies: number;
      authCookiesEchoed: boolean;
      error?: string;
      elapsed?: number;
    };
  };
  urls: {
    current: string;
    origin: string;
    apiBase: string;
    authEndpoints: {
      session: string;
      login: string;
      logout: string;
      refresh: string;
    };
  };
}

/**
 * Hook for generating comprehensive auth status reports
 */
export function useAuthStatusReport() {
  const { user, loading, error, isAuthenticated, isAdmin } = useServerAuth();

  const generateReport = useCallback(async (): Promise<AuthStatusReport> => {
    const timestamp = new Date().toISOString();
    const readinessStatus = getAuthReadinessStatus();

    // Environment detection
    const userAgent = navigator.userAgent;
    const platform = navigator.platform;

    // Test storage availability
    const testLocalStorage = () => {
      try {
        const testKey = 'auth-report-test';
        localStorage.setItem(testKey, 'test');
        localStorage.removeItem(testKey);
        return { available: true, quotaExceeded: false };
      } catch (error) {
        if (error instanceof DOMException && error.name === 'QuotaExceededError') {
          return { available: true, quotaExceeded: true };
        }
        return { available: false, quotaExceeded: false };
      }
    };

    const testSessionStorage = () => {
      try {
        const testKey = 'auth-report-test';
        sessionStorage.setItem(testKey, 'test');
        sessionStorage.removeItem(testKey);
        return { available: true, quotaExceeded: false };
      } catch (error) {
        if (error instanceof DOMException && error.name === 'QuotaExceededError') {
          return { available: true, quotaExceeded: true };
        }
        return { available: false, quotaExceeded: false };
      }
    };

    // Cookies analysis with echo test
    const cookieAnalysis = async () => {
      const cookiesEnabled = navigator.cookieEnabled;
      const documentCookies = document.cookie;
      const cookieCount = documentCookies ? documentCookies.split(';').length : 0;
      const hasAuthCookies = documentCookies.includes('session') ||
                            documentCookies.includes('auth') ||
                            documentCookies.includes('token');

      // Test SameSite support
      let sameSiteSupported = false;
      let sameSitePolicy = 'unknown';
      try {
        // Simple test for SameSite support
        sameSiteSupported = 'sameSite' in Document.prototype || document.cookie.includes('SameSite');
        sameSitePolicy = 'Lax'; // Default for most modern browsers
      } catch {
        sameSiteSupported = false;
      }

      // DV-AUTH-004: Cookie echo test
      let echoResult = {
        available: false,
        status: null as number | null,
        receivedCookies: 0,
        authCookiesEchoed: false,
        error: undefined as string | undefined,
        elapsed: undefined as number | undefined
      };

      try {
        const startTime = performance.now();
        const abortController = new AbortController();
        const timeoutId = setTimeout(() => abortController.abort(), 3000); // 3 second timeout

        const response = await fetch(buildApiUrl(API_ENDPOINTS.demo.cookiesEcho), {
          method: 'GET',
          credentials: 'include', // Send cookies
          headers: getApiHeadersGet(), // Preflight-safe headers
          signal: abortController.signal
        });

        clearTimeout(timeoutId);
        const elapsed = Math.round(performance.now() - startTime);

        echoResult.status = response.status;
        echoResult.elapsed = elapsed;

        if (response.ok) {
          const data = await response.json();
          echoResult.available = true;
          echoResult.receivedCookies = data.cookies ? Object.keys(data.cookies).length : 0;
          echoResult.authCookiesEchoed = data.cookies && (
            'session' in data.cookies ||
            'auth' in data.cookies ||
            'token' in data.cookies ||
            Object.keys(data.cookies).some(key => key.includes('auth') || key.includes('session'))
          );
        } else {
          echoResult.error = `HTTP ${response.status}: ${response.statusText}`;
        }
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          echoResult.error = 'Request timed out after 3 seconds';
        } else {
          echoResult.error = error instanceof Error ? error.message : 'Unknown error';
        }
      }

      return {
        document: {
          available: cookiesEnabled,
          count: cookieCount,
          hasAuthCookies
        },
        sameSite: {
          supported: sameSiteSupported,
          policy: sameSitePolicy
        },
        echo: echoResult
      };
    };

    // Network status
    const networkStatus = () => {
      const online = navigator.onLine;
      const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;

      return {
        online,
        connection: connection ? {
          effectiveType: connection.effectiveType,
          downlink: connection.downlink,
          rtt: connection.rtt
        } : undefined
      };
    };

    // CORS headers analysis
    const corsAnalysis = () => {
      return {
        credentials: 'include' as const,
        headers: {
          hasContentType: false, // GET requests should not have Content-Type
          hasAuthorization: false, // Cookie-based auth, no Authorization header
          customHeaders: [] // No custom headers for auth requests
        }
      };
    };

    // URL analysis
    const urlAnalysis = () => {
      const currentUrl = window.location.href;
      const origin = window.location.origin;
      const apiBase = import.meta.env.VITE_API_BASE_URL || 'https://api.hi-si.ai';

      return {
        current: currentUrl,
        origin,
        apiBase,
        authEndpoints: {
          session: `${apiBase}/api/auth/session`,
          login: `${apiBase}/api/auth/login`,
          logout: `${apiBase}/api/auth/logout`,
          refresh: `${apiBase}/api/auth/refresh`
        }
      };
    };

    const report: AuthStatusReport = {
      timestamp,
      session: {
        isAuthenticated,
        isLoading: loading,
        hasError: !!error,
        user: user ? {
          id: user.id,
          email: user.email,
          role: user.role,
          isAdmin
        } : undefined,
        error: error ? {
          type: error.type,
          message: error.userMessage,
          status: error.status,
          correlationId: error.correlationId
        } : undefined
      },
      readiness: readinessStatus,
      environment: {
        userAgent,
        platform,
        cookiesEnabled: navigator.cookieEnabled,
        javascriptEnabled: true, // Obviously true if this is running
        localStorage: testLocalStorage(),
        sessionStorage: testSessionStorage()
      },
      network: networkStatus(),
      cors: corsAnalysis(),
      cookies: await cookieAnalysis(), // DV-AUTH-004: Await async cookie analysis with echo test
      urls: urlAnalysis()
    };

    return report;
  }, [user, loading, error, isAuthenticated, isAdmin]);

  const generateFormattedReport = useCallback(async (): Promise<string> => {
    const report = await generateReport();

    const formatSection = (title: string, data: any, indent = 0): string => {
      const prefix = '  '.repeat(indent);
      let result = `${prefix}${title}:\n`;

      for (const [key, value] of Object.entries(data)) {
        if (typeof value === 'object' && value !== null) {
          result += formatSection(key, value, indent + 1);
        } else {
          result += `${prefix}  ${key}: ${JSON.stringify(value)}\n`;
        }
      }

      return result;
    };

    let formatted = `🔐 Auth Status Report - ${report.timestamp}\n\n`;

    formatted += formatSection('Session', report.session);
    formatted += formatSection('Auth Readiness', report.readiness);
    formatted += formatSection('Environment', report.environment);
    formatted += formatSection('Network', report.network);
    formatted += formatSection('CORS Policy', report.cors);
    formatted += formatSection('Cookies', report.cookies);
    formatted += formatSection('URLs', report.urls);

    formatted += '\n---\n';
    formatted += '🤖 Generated with Claude Code\n';
    formatted += `Report ID: auth-${Date.now()}-${Math.random().toString(36).substr(2, 8)}\n`;

    return formatted;
  }, [generateReport]);

  const copyToClipboard = useCallback(async (): Promise<boolean> => {
    try {
      console.log('🔄 Starting copy operation...');
      const report = await generateFormattedReport(); // DV-AUTH-004: Await async report generation with cookie echo
      console.log('✅ Report generated, length:', report.length);

      // DV-AUTH-004: Enhanced clipboard access with fallbacks
      // Method 1: Modern clipboard API (requires user interaction context)
      if (navigator.clipboard && window.isSecureContext) {
        try {
          console.log('🔄 Trying modern clipboard API...');
          await navigator.clipboard.writeText(report);
          console.log('✅ Modern clipboard API succeeded');
          return true;
        } catch (clipboardError) {
          console.warn('Clipboard API failed, trying fallback:', clipboardError);
          // Fall through to legacy method
        }
      }

      // Method 2: Legacy execCommand fallback
      console.log('🔄 Trying legacy execCommand fallback...');
      const textArea = document.createElement('textarea');
      textArea.value = report;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);

        if (successful) {
          console.log('✅ Legacy execCommand succeeded');
          return true;
        } else {
          console.error('❌ execCommand copy failed');
          return false;
        }
      } catch (execError) {
        document.body.removeChild(textArea);
        console.error('❌ execCommand copy threw error:', execError);
        return false;
      }
    } catch (error) {
      console.error('❌ Failed to copy auth status report to clipboard:', error);
      return false;
    }
  }, [generateFormattedReport]);

  return {
    generateReport,
    generateFormattedReport,
    copyToClipboard
  };
}